<template>
    <div class="search-view l-stack">
        <!-- Search -->
        <div class="l-padded u-bb">
            <SearchField v-model="search" />
        </div>

        <div v-if="username" class="l-padded u-bb">
            <strong>{{ $t('welcome', { username }) }}</strong>
        </div>

        <template v-if="search == ''">
            <ListHeader>
                <div slot="title">
                    {{ $t('shared.assets') }}
                </div>
            </ListHeader>

            <List>
                <router-link :to="{ name: 'allAssets' }">
                    <ListItem>
                        <div class="l-inline l-gap-2 l-center-v">
                            <TripMultipleDestinationsIcon
                                width="24"
                                height="24"
                            />

                            <span>
                                {{
                                    $t('allItems', {
                                        items: $t('shared.assets'),
                                    })
                                }}
                            </span>
                        </div>

                        <span>{{ trackers.length }}</span>
                    </ListItem>
                </router-link>

                <template v-if="isAlptracker">
                    <template
                        v-if="
                            fenceMonitoringTypeIds.length &&
                                fenceMonitoringCount.length
                        "
                    >
                        <router-link
                            :to="{
                                name: 'allAssets',
                                query: { type: fenceMonitoringTypeIds },
                            }"
                        >
                            <ListItem>
                                <div class="l-inline l-gap-2 l-center-v">
                                    <FenceGuardIcon width="24" height="24" />

                                    <span>
                                        {{ $t('fenceMonitoring') }}
                                    </span>
                                </div>

                                <span>{{ fenceMonitoringCount }}</span>
                            </ListItem>
                        </router-link>
                    </template>

                    <router-link
                        v-if="activeInLast6HoursAssets.length"
                        :to="{
                            name: 'allAssets',
                            query: { recent: true },
                        }"
                    >
                        <ListItem>
                            <div class="l-inline l-gap-2 l-center-v">
                                <RecentIcon
                                    width="24"
                                    height="24"
                                    color="black"
                                />

                                <span>
                                    {{ $t('recentlyActive') }}
                                </span>
                            </div>

                            <span>{{ activeInLast6HoursAssets.length }}</span>
                        </ListItem>
                    </router-link>
                </template>

                <router-link
                    v-if="alertAssets.length"
                    :to="{
                        name: 'allAssets',
                        query: { alert: true },
                    }"
                >
                    <ListItem>
                        <div class="l-inline l-gap-2 l-center-v">
                            <CheckIcon
                                v-if="!alertAssets.length"
                                width="24"
                                height="24"
                                color="#41b883"
                            />
                            <WarningIcon v-else width="24" height="24" />

                            <span>
                                {{
                                    $t('hasAlert', {
                                        items: $t('shared.assets'),
                                    })
                                }}
                            </span>
                        </div>

                        <span>{{ alertAssets.length }}</span>
                    </ListItem>
                </router-link>

                <router-link :to="{ name: 'assetTypes' }">
                    <ListItem>
                        <div class="l-inline l-gap-2 l-center-v">
                            <HierarchyIcon width="24" height="24" />

                            <span>{{ $t('router.assetTypes') }}</span>
                        </div>

                        <span>{{ assetTypesCount }}</span>
                    </ListItem>
                </router-link>
            </List>

            <ListHeader>
                <div slot="title">{{ $t('location') }}</div>
                <div slot="accessory">
                    <router-link :to="{ name: 'createlocation' }">
                        <IconButton>
                            <AddIcon
                                width="24"
                                height="24"
                                :name="$t('router.createlocation')"
                            />
                        </IconButton>
                    </router-link>
                </div>
            </ListHeader>

            <List>
                <router-link
                    v-for="location in locationsSortedByName"
                    :key="'loc' + location.id"
                    :to="{ name: 'location', params: { id: location.id } }"
                >
                    <ListItem>
                        <div class="l-inline l-gap-2 l-center-v">
                            <PinLocationIcon width="24" height="24" />

                            <span>{{ location.name }}</span>
                        </div>
                        <div class="l-inline l-gap-2 l-center-v">
                            <DisabledIcon
                                v-if="location.trailer_detection_disabled"
                                width="18"
                                height="18"
                                color="#e74c3c"
                                :name="$t('noTrailerDetection')"
                            />
                            <span>{{ locationAssetsCounts[location.id] }}</span>
                        </div>
                    </ListItem>
                </router-link>
            </List>

            <div v-if="isAlploraUser" class="u-bb">
                <router-link :to="{ name: 'globalAnimalActivityChart' }">
                    <ListItem>
                        <div class="l-inline l-gap-2 l-center-v">
                            <ActivityIcon width="24" height="24" />

                            <span>{{ $t('showActivity') }}</span>
                        </div>

                        <p><i class="arrow right" /></p>
                    </ListItem>
                </router-link>
            </div>

            <transition name="slide-from-right">
                <div v-if="isChartViewActive" class="detached-panel">
                    <div
                        class="l-inline l-center-v l-spread l-padded u-bb detached-panel-header"
                    >
                        {{ detachedPanelTitle }}

                        <router-link to=".">
                            <IconButton @click="$emit('expand')">
                                <RemoveIcon
                                    width="24"
                                    height="24"
                                    color="black"
                                />
                            </IconButton>
                        </router-link>
                    </div>

                    <div class="detached-panel-body">
                        <router-view />
                    </div>
                </div>
            </transition>
        </template>

        <!-- Search results -->
        <template v-else>
            <List>
                <router-link
                    v-for="item in filteredItems"
                    :key="'router' + item.vueKey"
                    :to="item.path"
                >
                    <ListItem :key="item.vueKey" class="u-bb">
                        <div class="l-stack l-gap-1">
                            <div>
                                <strong>{{ item.name }}</strong>
                            </div>

                            <div>{{ $t(item.type) }}</div>
                        </div>
                    </ListItem>
                </router-link>
            </List>
        </template>
    </div>
</template>

<script>
import { mapGetters, mapState } from 'vuex'

import ActivityIcon from '../icons/ActivityIcon'
import AddIcon from '../icons/AddIcon'
import CheckIcon from '../icons/CheckIcon'
import FenceGuardIcon from '../icons/FenceGuardIcon'
import HierarchyIcon from '../icons/HierarchyIcon'
import IconButton from '../IconButton'
import List from '../List'
import ListHeader from '../ListHeader'
import ListItem from '../ListItem'
import PinLocationIcon from '../icons/PinLocationIcon'
import RecentIcon from '../icons/RecentIcon'
import RemoveIcon from '../icons/RemoveIcon'
import SearchField from '../SearchField'
import TripMultipleDestinationsIcon from '../icons/TripMultipleDestinationsIcon'
import WarningIcon from '../icons/WarningIcon'
import DisabledIcon from '../icons/DisabledIcon.vue'

export default {
    name: 'SearchView',
    components: {
        ActivityIcon,
        AddIcon,
        CheckIcon,
        FenceGuardIcon,
        HierarchyIcon,
        IconButton,
        List,
        ListHeader,
        ListItem,
        PinLocationIcon,
        RecentIcon,
        RemoveIcon,
        SearchField,
        TripMultipleDestinationsIcon,
        WarningIcon,
        DisabledIcon,
    },
    data() {
        return {
            search: '',
            locationAssetsCounts: {},
        }
    },
    computed: {
        ...mapState('auth', ['hasActivityAccess', 'userInfo']),
        ...mapState('tracker', ['assetTypes', 'trackers']),
        ...mapGetters('location', ['locationsSortedByName']),
        ...mapGetters('tracker', [
            'activeInLast6HoursAssets',
            'alertAssets',
            'assetsByType',
        ]),
        assetTypesCount() {
            return this.assetTypes.filter(({ id }) => this.assetsByType()[id])
                .length
        },
        detachedPanelTitle() {
            return this.$t(`router.${this.$route.name}`)
        },
        fenceMonitoringTypeIds() {
            return this.assetTypes
                .filter(
                    type =>
                        type.identifier === 'fence-guard' ||
                        type.identifier === 'fency-boy'
                )
                .map(type => type.id)
        },
        fenceMonitoringCount() {
            return this.trackers.filter(
                tracker =>
                    tracker.asset_details.asset_type_type === 'fence-guard' ||
                    tracker.asset_details.asset_type_type === 'fency-boy'
            ).length
        },
        isChartViewActive() {
            return this.$route.name === 'globalAnimalActivityChart'
        },
        filteredItems() {
            if (!this.search) {
                return []
            }

            const filteredAssets = this.trackers
                .reduce(
                    ([tier1, tier2], asset) => {
                        if (
                            asset.asset_details.name
                                .toLowerCase()
                                .includes(this.search.toLowerCase())
                        ) {
                            tier1.push(asset)
                        } else if (
                            asset.deveui
                                .toLowerCase()
                                .includes(this.search.toLowerCase()) ||
                            asset.asset_details.identification
                                ?.toLowerCase()
                                .includes(this.search.toLowerCase())
                        ) {
                            tier2.push(asset)
                        }
                        return [tier1, tier2]
                    },
                    [[], []]
                )
                .flat()
                .map(asset => ({
                    id: asset.id,
                    vueKey: 'str' + asset.id,
                    name: asset.asset_details.name,
                    type: 'Asset',
                    path: {
                        name: 'detail',
                        params: { id: asset.id },
                    },
                }))

            const filteredLocations = this.locationsSortedByName
                .filter(location =>
                    location.name
                        .toLowerCase()
                        .includes(this.search.toLowerCase())
                )
                .map(location => ({
                    id: location.id,
                    vueKey: 'sloc' + location.id,
                    name: location.name,
                    type: 'location',
                    path: 'map/location/' + location.id,
                }))

            return [...filteredAssets, ...filteredLocations]
        },
        isAlploraUser() {
            return this.userInfo?.platform === 'alplora'
        },
        username() {
            return this.userInfo
                ? `${this.userInfo.first_name} ${this.userInfo.last_name}`.trim() ||
                      this.userInfo.username
                : ''
        },
        isAlptracker() {
            return process.env.VUE_APP_ENVIRONMENT_NAME === 'alptracker'
        },
    },
    beforeMount() {
        this.calculateLocationAssetsCounts()
    },
    methods: {
        calculateLocationAssetsCounts() {
            this.locationAssetsCounts = this.locationsSortedByName.reduce(
                (counts, location) => {
                    counts[location.id] = this.trackers.filter(
                        asset => asset.location === location.id
                    ).length
                    return counts
                },
                {}
            )
        },
    },
}
</script>

<i18n>
{
    "en": {
        "allItems": "All {items}",
        "fenceMonitoring": "Fence monitoring",
        "hasAlert": "{items} with alert",
        "location": "Location",
        "lowBattery": "Low battery",
        "recentlyActive": "Active in the last 6 hours",
        "showActivity": "Show activity",
        "tracker": "Tracker",
        "welcome": "Welcome, {username}!",
        "noTrailerDetection": "Trailer detection disabled"
    },
    "de": {
        "allItems": "Alle {items}",
        "fenceMonitoring": "Zaunüberwachungen",
        "hasAlert": "{items} mit Alarm",
        "location": "Standort",
        "lowBattery": "Niedriger Batteriestand",
        "recentlyActive": "Aktiv in den letzten 6 Stunden",
        "showActivity": "Aktivität anzeigen",
        "tracker": "Tracker",
        "welcome": "Willkommen, {username}!",
        "noTrailerDetection": "Anhängererkennung deaktiviert"
    },
    "fr": {
        "allItems": "Tous {items}",
        "fenceMonitoring": "Surveillance des clôtures",
        "hasAlert": "{items} avec alarme",
        "location": "Emplacement",
        "lowBattery": "Batterie faible",
        "recentlyActive": "Actif au cours des 6 dernières heures",
        "showActivity": "Afficher l'activité",
        "tracker": "Tracker",
        "welcome": "Bienvenue, {username} !",
        "noTrailerDetection": "Détection de remorque désactivée"
    },
    "it": {
        "allItems": "Tutti {items}",
        "fenceMonitoring": "Monitoraggio della recinzione",
        "hasAlert": "{items} con allarme",
        "location": "Locazione",
        "lowBattery": "Basso Batteria",
        "recentlyActive": "Attivo nelle ultime 6 ore",
        "showActivity": "Mostra attività",
        "tracker": "Tracker",
        "welcome": "Benvenuto, {username}!",
        "noTrailerDetection": "Rilevamento rimorchio disabilitato"
    }
}
</i18n>

<style lang="scss" scoped>
.search-view > * + * {
    border-bottom: $style-border;
}
</style>
